import { alpha, Palette as MuiPalette } from '@mui/material';

import { tokenColors } from './tokenColors';

export const tokenPaletteMain: Partial<MuiPalette> = {
  background: {
    brand: tokenColors.primary.main,
    default: tokenColors.primary.white,
    accent: tokenColors.grey[245],
    /* transferred from main.ts for typescript */
    paper: tokenColors.primary.white,
    /* added, not in figma design */
    defaultDisabled: tokenColors.grey[148],
    secondary: tokenColors.primary.white,
  },
  surface: {
    default: tokenColors.primary.white,
    accentDefault: tokenColors.grey[245],
    accentHover: tokenColors.grey[240],
    accentActive: tokenColors.grey[233],
    accentDisabled: tokenColors.grey[227],
    primaryDefault: tokenColors.primary.main,
    primaryHover: tokenColors.primary.dark,
    primaryActive: tokenColors.primary.darker,
    primaryDisabled: tokenColors.grey[227],
    tertiaryDefault: alpha(tokenColors.primary.black, 0),
    tertiaryHover: alpha(tokenColors.primary.black, 0.03),
    tertiaryActive: alpha(tokenColors.primary.black, 0.06),
    advice: tokenColors.aqua.lighter,
    success: tokenColors.mint.lighter,
    warning: tokenColors.peach.lighter,
    error: tokenColors.berry.lighter,
    positive: tokenColors.garden.dark,
    indeterminate: tokenColors.grey[102],
  },
  border: {
    default: tokenColors.grey[227],
    defaultFocus: tokenColors.aqua.dark,
    accent: tokenColors.grey[68],
    success: tokenColors.mint.dark,
    warning: tokenColors.peach.main,
    error: tokenColors.berry.main,
    secondaryDefault: tokenColors.grey[86],
    secondaryHover: tokenColors.grey[68],
    secondaryActive: tokenColors.grey[41],
    secondaryDisabled: tokenColors.grey[148],
    positive: tokenColors.garden.dark,
    indeterminate: tokenColors.grey[102],
    /* transferred from main.ts to fix undefined */
    main: tokenColors.grey[148],
    hover: tokenColors.grey[41],
  },
  text: {
    primary: tokenColors.primary.black,
    primaryDisabled: tokenColors.grey[68],
    secondary: tokenColors.grey[68],
    secondaryDisabled: tokenColors.grey[148],
    success: tokenColors.mint.dark,
    warning: tokenColors.peach.main,
    error: tokenColors.berry.main,
    accent: tokenColors.primary.dark,
    indeterminate: tokenColors.grey[148],
    positive: tokenColors.garden.dark,
    negative: tokenColors.berry.dark,
    defaultDisabled: tokenColors.grey[148],
    linkDefault: tokenColors.primary.dark,
    linkHover: tokenColors.primary.darker,
    linkActive: tokenColors.primary.darker,
    linkDisabled: tokenColors.grey[148],
    /* transferred from main.ts for typescript */
    headline: tokenColors.primary.white,
    link: tokenColors.primary.main,
    overline: tokenColors.primary.main,
    disabled: '#6E6E6E', // no fitting primitive
    breadcrumb: tokenColors.grey[68],
    linda: tokenColors.grey[68],
    unit: tokenColors.grey[148],
  },
  labeling: {
    default: tokenColors.primary.black,
    defaultFocus: tokenColors.aqua.dark,
    defaultDisabled: tokenColors.grey[148],
    defaultInverted: tokenColors.primary.white,
    advice: tokenColors.aqua.darker,
    success: tokenColors.mint.main,
    warning: tokenColors.peach.main,
    error: tokenColors.berry.main,
    primaryDefault: tokenColors.primary.white,
    primaryHover: tokenColors.primary.white,
    primaryActive: tokenColors.primary.white,
    primaryDisabled: tokenColors.grey[153],
    secondaryDefault: tokenColors.grey[41],
    secondaryHover: tokenColors.grey[34],
    secondaryActive: tokenColors.primary.black,
    secondaryDisabled: tokenColors.grey[148],
    tertiaryDefault: tokenColors.grey[41],
    tertiaryHover: tokenColors.grey[34],
    tertiaryActive: tokenColors.primary.black,
    inputDefault: tokenColors.grey[86],
    inputHover: tokenColors.grey[41],
    inputActive: tokenColors.grey[41],
    inputDisabled: tokenColors.grey[148],
  },
};

export const tokenPaletteLight = {
  /* transferred from light.ts for typescript */
  background: {
    default: '#FAFAFA', // no fitting primitive
  },
  text: {
    overline: tokenColors.primary.dark,
    link: tokenColors.primary.dark,
  },
};

export const tokenPaletteDark: Partial<MuiPalette> = {
  background: {
    brand: tokenColors.primary.main,
    default: tokenColors.grey[41],
    accent: tokenColors.grey[34],
    secondary: tokenColors.grey[34],
    /* transferred from dark.ts for typescript */
    paper: tokenColors.grey[34],
    /* added, not in figma design */
    defaultDisabled: tokenColors.grey[148],
  },
  surface: {
    default: tokenColors.grey[41],
    accentDefault: tokenColors.grey[68],
    accentHover: tokenColors.grey[86],
    accentActive: tokenColors.grey[102],
    accentDisabled: tokenColors.grey[68],
    primaryDefault: tokenColors.primary.main,
    primaryHover: tokenColors.night.main,
    primaryActive: tokenColors.night.dark,
    primaryDisabled: tokenColors.grey[217],
    tertiaryDefault: alpha(tokenColors.primary.white, 0),
    tertiaryHover: alpha(tokenColors.primary.white, 0.03),
    tertiaryActive: alpha(tokenColors.primary.white, 0.06),
    advice: tokenColors.aqua.light,
    success: tokenColors.mint.light,
    warning: tokenColors.peach.light,
    error: tokenColors.berry.light,
    positive: tokenColors.garden.light,
    indeterminate: tokenColors.grey[233],
  },
  border: {
    default: tokenColors.grey[68],
    defaultFocus: tokenColors.aqua.light,
    accent: tokenColors.grey[227],
    success: tokenColors.mint.light,
    warning: tokenColors.peach.light,
    error: tokenColors.berry.light,
    secondaryDefault: tokenColors.grey[227],
    secondaryHover: tokenColors.grey[233],
    secondaryActive: tokenColors.grey[240],
    secondaryDisabled: tokenColors.grey[148],
    positive: tokenColors.garden.light,
    indeterminate: tokenColors.grey[233],
    /* transferred from dark.ts to fix undefined */
    main: tokenColors.grey[190],
    hover: tokenColors.primary.white,
  },
  text: {
    primary: tokenColors.primary.white,
    primaryDisabled: tokenColors.grey[227],
    secondary: tokenColors.grey[227],
    secondaryDisabled: tokenColors.grey[148],
    success: tokenColors.mint.light,
    warning: tokenColors.peach.light,
    error: tokenColors.berry.light,
    accent: tokenColors.grey[227],
    indeterminate: tokenColors.grey[233],
    positive: tokenColors.garden.light,
    negative: tokenColors.berry.light,
    defaultDisabled: tokenColors.grey[148],
    linkDefault: tokenColors.grey[245],
    linkHover: tokenColors.primary.white,
    linkActive: tokenColors.primary.white,
    linkDisabled: tokenColors.grey[148],
    /* transferred from dark.ts for typescript */
    headline: tokenColors.primary.white,
    link: tokenColors.primary.main,
    overline: tokenColors.grey[190],
    disabled: tokenColors.grey[148],
    breadcrumb: tokenColors.grey[240],
    linda: tokenColors.grey[68],
    unit: tokenColors.grey[148],
  },
  labeling: {
    default: tokenColors.primary.white,
    defaultFocus: tokenColors.aqua.light,
    defaultDisabled: tokenColors.grey[148],
    defaultInverted: tokenColors.primary.black,
    advice: tokenColors.aqua.main,
    success: tokenColors.mint.light,
    warning: tokenColors.peach.light,
    error: tokenColors.berry.light,
    primaryDefault: tokenColors.primary.white,
    primaryHover: tokenColors.primary.white,
    primaryActive: tokenColors.primary.white,
    primaryDisabled: tokenColors.grey[153],
    secondaryDefault: tokenColors.grey[240],
    secondaryHover: tokenColors.grey[245],
    secondaryActive: tokenColors.primary.white,
    secondaryDisabled: tokenColors.grey[148],
    tertiaryDefault: tokenColors.grey[240],
    tertiaryHover: tokenColors.grey[245],
    tertiaryActive: tokenColors.primary.white,
    inputDefault: tokenColors.grey[227],
    inputHover: tokenColors.grey[240],
    inputActive: tokenColors.grey[240],
    inputDisabled: tokenColors.grey[148],
  },
};
